import {selectRootElementWithFocusedInstancesExpanded} from '@packages/utilities/platform-selectors';
import {type DesignerStateType} from '@packages/systems/core/records/DesignerState';
import {type UiNodeStateType} from '@packages/systems/core/records/UiNodeState';
import {
  convertElementIdToNativeId,
  generateInstanceNativeId,
  type NativeId,
} from '@packages/systems/core/utils/NativeIdUtils';
import {countAncestorProjectingElements} from './TraversalUtils';
import {Expression} from '@packages/systems/wfdl-ast';

/**
 * Returns the nativeIdPath of the currently selected node.
 * Useful for cases where we have to generate a native id out of an element without any other information
 * Note: this only returns the correct nativeId for the currently focused component.
 * Additionally this will only return the instance suffix with `0` as the indices.
 * @param elementId
 * @param designerState
 * @param uiNodeState
 */
export const generateNativeIdFromElement = (
  elementId: string,
  designerState: DesignerStateType,
  uiNodeState: UiNodeStateType
): NativeId => {
  // selectRootElementWithFocusedInstancesExpanded
  const root = selectRootElementWithFocusedInstancesExpanded({
    DesignerStore: designerState,
    UiNodeStore: uiNodeState,
  });

  if (root) {
    return generateNativeIdFromElementInExpression(elementId, root);
  }

  return convertElementIdToNativeId(elementId);
};

export function generateNativeIdFromElementInExpression(
  elementId: string,
  root: Expression
): NativeId {
  const projectionDepth = countAncestorProjectingElements(elementId, root);
  return projectionDepth > 0
    ? generateInstanceNativeId(elementId, Array(projectionDepth).fill(0))
    : convertElementIdToNativeId(elementId);
}
